import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { ClaimRewardsError, useClaimRewards } from "@/hooks/user/useClaimRewards";

type UseClaimCashbackRewardsProps = {
  onClaimSuccess?: () => void;
  onClaimError?: (error: ClaimRewardsError) => void;
};

/**
 * Hook for claiming Cashback rewards only.
 * Only fetches claimable data (needed for calldata/proof to execute the claim transaction).
 * Claimable-soon data is not fetched here as it's already available in useCashbackStatus.
 */
export const useClaimCashbackRewards = ({ onClaimSuccess, onClaimError }: UseClaimCashbackRewardsProps) => {
  const apiHelper = useApi();
  const isClaimsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.CLAIMS_HUB_ENABLE);

  return useClaimRewards({
    onClaimSuccess,
    onClaimError,
    queryKeys: {
      claimableRewards: QueryKeys.HUB_REWARDS_CLAIMABLE_CASHBACK,
      // No claimableSoonRewards - data already available in useCashbackStatus
    },
    fetchers: {
      getClaimableRewards: async (userAddress) => {
        // Backend filters by campaignTypes - no client-side filtering needed
        const claimableRewards = await apiHelper.getOrThrow(API_ENDPOINT.HUB_REWARDS_CLAIMABLE, {
          queryParams: { defiAddress: userAddress, campaignTypes: "CASHBACK" },
        });

        return claimableRewards.claimableEpochs;
      },
      simulateClaim: async (claimItems) => {
        await apiHelper.postOrThrow(API_ENDPOINT.HUB_REWARDS_SIMULATE, {
          bodyParams: { claimItems },
        });
      },
      // No getClaimableSoonRewards - data already available in useCashbackStatus
    },
    metricsEvent: METRICS_EVENTS.CLAIM_CASHBACK_REWARDS,
    enabled: isClaimsEnabled,
  });
};
